<template>
  <div
    class="kb-row"
    :class="{ 'kb-assoc-row': subkrewe && !isLoading }"
  >
    <div class="min-w-0">
      <h4 class="kb-row-title">
        {{ title }}
      </h4>
      <p
        v-if="isLoading"
        class="kb-row-hint"
      >
        <KbSpinner class="mr-1.5" />
        Loading SubKrewe…
      </p>

      <!-- When a subkrewe is assigned: a card that opens it -->
      <div
        v-else-if="subkrewe"
        class="kb-assoc-card"
      >
        <button
          type="button"
          class="kb-assoc-open"
          @click="onViewSubkrewe"
        >
          <Icon
            name="lucide:umbrella"
            class="kb-assoc-icon"
            aria-hidden="true"
          />
          <span class="kb-assoc-name">{{ subkrewe.name }}</span>
          <i
            class="pi pi-arrow-up-right kb-assoc-go"
            aria-hidden="true"
          />
        </button>
        <div class="kb-assoc-foot">
          <span class="kb-row-meta">
            <template v-if="isCountLoading">counting…</template>
            <template v-else><b class="text-kb-fg">{{ memberCount }}</b> {{ memberCount === 1 ? 'member' : 'members' }}</template>
          </span>
          <span
            class="kb-tag"
            :class="approvalTag.class"
          >{{ approvalTag.label }}</span>
          <Button
            v-tooltip.left="removeTooltip"
            type="button"
            label="Remove"
            icon="pi pi-trash"
            text
            severity="danger"
            size="small"
            class="ml-auto"
            :aria-label="removeTooltip"
            :loading="isRemoving"
            :disabled="isRemoving"
            @click="onRemoveFromSubkrewe"
          />
        </div>
      </div>

      <!-- When no subkrewe is assigned -->
      <p
        v-else
        class="kb-row-hint"
      >
        {{ emptyMessage }}
      </p>
    </div>

    <div
      v-if="!isLoading && !subkrewe"
      class="kb-row-aside"
    >
      <Button
        type="button"
        :label="assignButtonLabel"
        icon="pi pi-search"
        outlined
        severity="secondary"
        size="small"
        @click="onShowAssignDialog"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SubKrewe } from '~/shared/types/subkrewe'
import { getParadeApprovalStatus } from '~/shared/utils/subkreweApproval'

interface Props {
  title: string
  subkrewe: SubKrewe | null
  memberCount: number
  isLoading: boolean
  isCountLoading?: boolean
  isCaptain?: boolean
  emptyMessage?: string
  assignButtonLabel?: string
  removeTooltip?: string
  /** The removal is saving (set by the parent, which asks for confirmation first) */
  isRemoving?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isCaptain: false,
  isRemoving: false,
  emptyMessage: 'No SubKrewe assigned',
  assignButtonLabel: 'Assign to SubKrewe',
  removeTooltip: 'Remove from SubKrewe',
})

// For a band this is its approval for the current parade
const { paradeYear } = useParadeYear()
const approvalStatus = computed(() => getParadeApprovalStatus(props.subkrewe, paradeYear.value))
const approvalTag = computed(() => {
  switch (approvalStatus.value) {
    case 'Yes': return { label: 'Approved', class: 'kb-tag-ok' }
    case 'No': return { label: 'Not approved', class: 'kb-tag-warn' }
    default: return { label: 'Awaiting approval', class: 'kb-tag-neutral' }
  }
})

// Define events that parent will listen for
const emit = defineEmits(['view-subkrewe', 'remove-from-subkrewe', 'show-assign-dialog'])

// Handler functions that emit the appropriate events
const onViewSubkrewe = () => {
  emit('view-subkrewe')
}

const onRemoveFromSubkrewe = () => {
  emit('remove-from-subkrewe')
}

const onShowAssignDialog = () => {
  emit('show-assign-dialog')
}
</script>

<style scoped>
/* With a SubKrewe the row is one column: the title, then the card */
.kb-assoc-row {
  grid-template-columns: minmax(0, 1fr);
}

/* The SubKrewe as a card, a step lighter than the section it sits in */
.kb-assoc-card {
  display: grid;
  gap: 0.625rem;
  margin-top: 0.5rem;
  padding: 0.75rem 0.875rem 0.5rem;
  border: 1px solid var(--kb-line);
  border-radius: 4px;
  background: var(--kb-pane);
}

.kb-assoc-open {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0;
  border: 0;
  background: none;
  color: var(--kb-fg);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.kb-assoc-icon {
  flex: 0 0 auto;
  margin-top: 0.1875rem;
  font-size: 1rem;
  color: var(--kb-dim);
}

.kb-assoc-name {
  min-width: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.kb-assoc-go {
  flex: 0 0 auto;
  margin-top: 0.3125rem;
  margin-left: auto;
  font-size: 0.6875rem;
  color: var(--kb-dim);
}

.kb-assoc-open:hover .kb-assoc-name,
.kb-assoc-open:hover .kb-assoc-go {
  color: var(--kb-accent);
}

.kb-assoc-open:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 3px;
  border-radius: 2px;
}

/* Members and approval, with Remove at the end */
.kb-assoc-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--kb-line);
}
</style>
